import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './pages/Dashboard/Dashboard';
import Transformations from './pages/Transformations/Transformations';
import APIIntegration from './pages/Transformations/APIIntegration';
import SoapHistory from './pages/SoapHistory/SoapHistory';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProxyLogs from './pages/ProxyLogs/ProxyLogs';
import CustomAnalytics from './pages/CustomAnalytics/CustomAnalytics';
import Partners from './pages/Partners/Partners';
import AS2Routing from './pages/AS2Routing/AS2Routing';
import AS2Connectors from './pages/AS2Connectors/AS2Connectors';
import AS2Metrics from './pages/AS2Metrics/AS2Metrics';
import FileTransmissions from './pages/FileTransmissions/FileTransmissions';
import RelayOverview from './pages/Relay/RelayOverview';
import Settings from './pages/Settings/Settings';
import OrganizationIntake from './pages/Settings/OrganizationIntake';
import Onboarding from './pages/Auth/Onboarding';
import SendFile from './pages/SendFile/SendFile';
import EPCISRules from './pages/EPCISRules/EPCISRules';
import DataExplorer from './pages/DataExplorer/DataExplorer';
import Chatbot from './components/Chatbot/Chatbot';
import './App.css';

/**
 * Shared layout wrapper for all protected pages.
 * Renders sidebar, mobile header, main content, and the floating chatbot.
 */
function AppLayout({ children, activeTab, setActiveTab, isSidebarOpen, setIsSidebarOpen }) {
  return (
    <div className="app">
      <div className="mobile-header">
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsSidebarOpen(true)}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>
        <span className="mobile-logo">TITAN</span>
      </div>
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <main className="main-content">
        {children}
      </main>
      <Chatbot />
    </div>
  );
}

/**
 * Main App Component
 * Sets up routing and layout structure
 */
function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />

        {/* Protected Routes — all share AppLayout which includes <Chatbot /> */}
        <Route path="/dashboard" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><Dashboard setActiveTab={setActiveTab} /></AppLayout></ProtectedRoute>} />
        <Route path="/transformations" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><Transformations setActiveTab={setActiveTab} /></AppLayout></ProtectedRoute>} />
        <Route path="/transformations/api-integration" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><APIIntegration setActiveTab={setActiveTab} /></AppLayout></ProtectedRoute>} />
        <Route path="/soap-history" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><SoapHistory setActiveTab={setActiveTab} /></AppLayout></ProtectedRoute>} />
        <Route path="/logs" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><ProxyLogs /></AppLayout></ProtectedRoute>} />
        <Route path="/custom-analytics" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><CustomAnalytics /></AppLayout></ProtectedRoute>} />
        <Route path="/partners" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><Partners /></AppLayout></ProtectedRoute>} />
        <Route path="/as2-routing" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><AS2Routing /></AppLayout></ProtectedRoute>} />
        <Route path="/as2-metrics" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><AS2Metrics /></AppLayout></ProtectedRoute>} />
        <Route path="/as2-connectors" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><AS2Connectors /></AppLayout></ProtectedRoute>} />
        <Route path="/file-transmissions" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><FileTransmissions /></AppLayout></ProtectedRoute>} />
        <Route path="/relay/overview" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><RelayOverview /></AppLayout></ProtectedRoute>} />
        <Route path="/send-file" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><SendFile /></AppLayout></ProtectedRoute>} />
        <Route path="/epcis-rules" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><EPCISRules /></AppLayout></ProtectedRoute>} />
        <Route path="/data-explorer" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><DataExplorer /></AppLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><Settings /></AppLayout></ProtectedRoute>} />
        <Route path="/settings/intake" element={<ProtectedRoute><AppLayout activeTab={activeTab} setActiveTab={setActiveTab} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen}><OrganizationIntake /></AppLayout></ProtectedRoute>} />

        <Route path="/onboarding" element={<Onboarding />} />

      </Routes>
    </Router>
  );
}

export default App;
